/**
 * What "launch" means for each edition, as data.
 *
 * The launch sequence itself (promote the draft, upload the stimuli, hand back
 * the share link) is identical in both products and lives in `useWizardDraft`.
 * What differs is the payload: Pro sends everything the four steps collected,
 * FaceWise sends one name, one material and the fixed metric — and forces off
 * the knobs its screen never offered.
 */
import type { CreateProjectRequest } from '@/services/projectTypes'
import type { EditableQuestion } from '@/components/questions/types'
import { askableQuestions } from './askable'
import type { StudyDraft } from './types'

export type LaunchPlan = {
  /** Questions persisted as the study's own (hidden) questionnaire, in order.
   *  Empty leaves `fields.questionnaire_template` as the study's questionnaire. */
  questions: EditableQuestion[]
  /** The project columns the launch writes. */
  fields: Partial<CreateProjectRequest> & { name: string }
  /** Address-book people to email a tracked invite once the study is live. */
  invite?: { respondentIds: number[]; tagIds: number[] }
}

function targetFields(value: string): Partial<CreateProjectRequest> {
  const target = Math.round(Number(value))
  return Number.isFinite(target) && target > 0 ? { target_respondents: target } : {}
}

/** The same number as a hard stop rather than a goal.
 *
 *  Only FaceWise sends it. Pro's distribution step has carried a response-limit
 *  field that has never reached the backend, defaulted to 150 — starting to
 *  honour it now would quietly cap every Pro study somebody launches next.
 *  Pro's field stays as it is until that is a decision somebody makes on purpose.
 */
function limitField(value: string): Partial<CreateProjectRequest> {
  const limit = Math.round(Number(value))
  return Number.isFinite(limit) && limit > 0 ? { response_limit: limit } : {}
}

/** FaceWise Pro: the study exactly as the four steps configured it. */
export function buildProLaunchPlan(draft: StudyDraft, fallbackName: string): LaunchPlan {
  return {
    questions: askableQuestions(draft.questions, draft.demographics),
    fields: {
      name: draft.name.trim() || fallbackName,
      description: draft.description.trim(),
      client: draft.client.trim(),
      ...targetFields(draft.targetRespondents),
      questionnaire_template: draft.questionnaireTemplateId,
      question_timing: draft.questionTiming,
      require_access_code: draft.requireAccessCode,
      access_code: draft.requireAccessCode ? draft.accessCode.trim() : '',
      eye_tracking_enabled: draft.eyeTracking,
      calibration_variant: draft.calibrationVariant,
      is_public: draft.isPublic,
    },
    invite: { respondentIds: draft.inviteRespondentIds, tagIds: draft.inviteTagIds },
  }
}

/**
 * FaceWise: one measurement. The study is unlisted rather than public — anyone
 * with the link can take it, but it never appears in a respondent panel — and
 * carries no access code and no email invitations. `Project.save()` forces the
 * same three columns server-side; sending them explicitly keeps the request
 * honest about what the customer was shown rather than relying on the backend
 * to correct us.
 */
export function buildBasicLaunchPlan(
  draft: StudyDraft,
  fixedMetric: EditableQuestion[],
  fallbackName: string,
): LaunchPlan {
  return {
    questions: fixedMetric,
    fields: {
      name: draft.name.trim() || fallbackName,
      // The response limit is how many people this measurement wants. It is
      // both the study's target — the number the respondent counter and the
      // study dashboard read — and a hard stop: once that many have started,
      // the link stops accepting anybody new.
      ...targetFields(draft.responseLimit),
      ...limitField(draft.responseLimit),
      question_timing: 'end',
      require_access_code: false,
      access_code: '',
      eye_tracking_enabled: false,
      is_public: false,
      ...(draft.expiry ? { expires_at: draft.expiry } : {}),
    },
  }
}
